import React, { useState, useRef, useEffect } from 'react';
import {
  Loader2,
  Maximize2,
  Minimize2,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
} from 'lucide-react';

interface HTMLContentPlayerProps {
  htmlUrl: string;
  audioUrl?: string;
  title: string;
  duration?: number; // in minutes
  isPlaying: boolean;
  playbackRate: number;
  volume: number;
  onPlayPause: () => void;
  onPlaybackRateChange: () => void;
  onVolumeChange: (volume: number) => void;
  onProgressChange?: (progressPercentage: number) => void;
  onContentComplete?: () => void;
  /** How far through the narration, 0-1. A RATIO, not seconds: the same lesson
   *  is a different length in every language, so only the proportion carries
   *  across a language switch. Same contract as CustomVideoPlayer. */
  onProgressRatio?: (ratio: number, playing: boolean) => void;
  /** Seek here (0-1) once the length is known, to resume after a switch. */
  initialRatio?: number;
  /** Resume playing once that seek lands — the learner was already listening. */
  autoPlayOnResume?: boolean;
  // When set, the content is an uploaded PDF: embed it directly in a
  // NON-sandboxed iframe (Chrome disables its PDF viewer inside a sandboxed
  // frame), instead of loading htmlUrl in the usual sandboxed iframe.
  pdfUrl?: string;
}

export default function HTMLContentPlayer({
  htmlUrl,
  audioUrl,
  title,
  duration = 1,
  isPlaying,
  playbackRate,
  volume,
  onPlayPause,
  onPlaybackRateChange,
  onVolumeChange,
  onProgressChange,
  onContentComplete,
  onProgressRatio,
  initialRatio,
  autoPlayOnResume,
  pdfUrl,
}: HTMLContentPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);

  // Read ONCE at mount — a language switch remounts the player, so "mount" is
  // exactly when a resume applies. Applied a single time so nothing later can
  // drag the learner back to it.
  const initial_ratio_ref = useRef(initialRatio);
  const auto_play_ref = useRef(autoPlayOnResume);
  const resume_applied_ref = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenCheckRef = useRef<number>();
  useEffect(() => () => window.clearTimeout(fullscreenCheckRef.current), []);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // iPhone Safari has no element Fullscreen API (only <video> can go
  // fullscreen), so there the player instead covers the page with CSS.
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const isExpanded = isFullscreen || isPseudoFullscreen;
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumePopup, setShowVolumePopup] = useState(false);
  const volumeRef = useRef<HTMLDivElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  // False until the audio metadata (duration) is known — used to show a loader
  // instead of a misleading 0:00 while the audio is still loading.
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasCompletedOnce, setHasCompletedOnce] = useState(false);
  const [viewStartTime] = useState(Date.now());
  const [audioProgress, setAudioProgress] = useState(0);
  const [viewTimeProgress, setViewTimeProgress] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Reset audio-loading state whenever the source changes.
  useEffect(() => {
    setIsAudioReady(false);
    setCurrentTime(0);
    setAudioDuration(0);
    setProgress(0);
  }, [audioUrl]);

  // Handle audio playback
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        // Can be refused (autoplay rules); the tap handler below has already
        // started it in that case, so a rejection here is not an error.
        audioRef.current.play().catch(() => {});
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Track viewing time progress (engagement time)
  useEffect(() => {
    if (hasCompletedOnce) return;

    const minViewTimeMs = duration * 60 * 1000 * 0.8; // 80% of estimated duration
    const interval = setInterval(() => {
      const elapsed = Date.now() - viewStartTime;
      const viewProgress = Math.min(100, (elapsed / minViewTimeMs) * 100);
      setViewTimeProgress(viewProgress);
    }, 1000);

    return () => clearInterval(interval);
  }, [duration, viewStartTime, hasCompletedOnce]);

  // Track scroll progress inside iframe
  useEffect(() => {
    if (hasCompletedOnce) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleIframeScroll = () => {
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDocument) return;

        const scrollTop = iframeDocument.documentElement.scrollTop || iframeDocument.body.scrollTop;
        const scrollHeight = iframeDocument.documentElement.scrollHeight || iframeDocument.body.scrollHeight;
        const clientHeight = iframeDocument.documentElement.clientHeight || window.innerHeight;

        // Calculate scroll percentage
        const scrollableHeight = scrollHeight - clientHeight;
        if (scrollableHeight > 0) {
          const scrollPercentage = Math.min(100, (scrollTop / scrollableHeight) * 100);
          setScrollProgress(scrollPercentage);

          // If scrolled to 90% or more, consider it fully scrolled
          if (scrollPercentage >= 90) {
            setScrollProgress(100);
          }
        } else {
          // Content fits in viewport, no scrolling needed
          setScrollProgress(100);
        }
      } catch (error) {
        // Cross-origin iframe - can't access content
        // Fall back to time-based progress only
      }
    };

    // Try to attach scroll listener after iframe loads
    const attachScrollListener = () => {
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDocument) {
          iframeDocument.addEventListener('scroll', handleIframeScroll);
          // Initial check
          handleIframeScroll();
        }
      } catch (error) {
        // Cannot attach scroll listener (cross-origin iframe)
      }
    };

    iframe.addEventListener('load', attachScrollListener);

    // Try immediately in case already loaded
    attachScrollListener();

    return () => {
      try {
        const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
        if (iframeDocument) {
          iframeDocument.removeEventListener('scroll', handleIframeScroll);
        }
      } catch (error) {
        // Ignore cleanup errors
      }
    };
    // htmlUrl: the iframe is re-created per URL, so the listener must re-attach.
  }, [hasCompletedOnce, htmlUrl]);

  // Report progress changes when scroll or time changes (not just during audio playback)
  useEffect(() => {
    if (!onProgressChange) return;

    let reportedProgress: number;

    if (audioUrl) {
      // With audio: Use the MAXIMUM of scroll, audio, or time progress
      reportedProgress = Math.floor(Math.max(scrollProgress, audioProgress, viewTimeProgress));
    } else {
      // No audio: Use the MAXIMUM of scroll or time progress
      reportedProgress = Math.floor(Math.max(scrollProgress, viewTimeProgress));
    }

    // Cap at 100%
    reportedProgress = Math.min(100, reportedProgress);

    onProgressChange(reportedProgress);
  }, [scrollProgress, viewTimeProgress, audioProgress, audioUrl, onProgressChange]);

  // Check for completion: requires scroll to bottom (90%+) OR time-based fallback
  // Audio progress is reported but scrolling to bottom is the primary completion trigger
  useEffect(() => {
    if (hasCompletedOnce) return;

    // Primary completion: User scrolled through the content (90%+)
    const scrollConditionMet = scrollProgress >= 90;

    // Fallback completion: User spent enough time (for cross-origin iframes where scroll tracking fails)
    const timeConditionMet = viewTimeProgress >= 100;

    const audioConditionMet = audioUrl ? audioProgress >= 90 : true;

    // Complete if EITHER scrolled to bottom OR spent enough time with audio
    const shouldComplete = scrollConditionMet || (timeConditionMet && audioConditionMet);

    if (shouldComplete && onContentComplete) {
      setHasCompletedOnce(true);
      onContentComplete();
    }
  }, [audioProgress, viewTimeProgress, scrollProgress, audioUrl, hasCompletedOnce]);
  // Note: onContentComplete intentionally excluded from dependencies to prevent infinite loops

  // Handle playback rate changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Phone volume pop-up closes on a tap anywhere outside it.
  useEffect(() => {
    if (!showVolumePopup) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (!volumeRef.current?.contains(e.target as Node)) setShowVolumePopup(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [showVolumePopup]);

  // iPhone (every iOS browser) starts audio only from inside the tap itself —
  // the effect above runs after a re-render, which iOS may refuse. iOS also
  // loads nothing (not even the length) before that first play, so the button
  // must not wait for the audio to be ready: that is what it would wait for.
  const handlePlayPauseClick = () => {
    const audio = audioRef.current;
    if (audio && !isPlaying) {
      audio.play().catch((error) => {
        console.warn('Audio play was refused:', error);
        // Refused (not just interrupted by a quick pause): flip the button back
        // so it doesn't show "pause" over silence.
        if (error?.name === 'NotAllowedError' || error?.name === 'NotSupportedError') {
          onPlayPause();
        }
      });
    }
    onPlayPause();
  };

  const toggleMute = () => {
    // Unmuting at volume 0 would stay silent, so bring the level back up.
    if (isMuted && volume === 0) onVolumeChange(1);
    setIsMuted(m => !m);
  };

  const renderVolumeSlider = (widthClass: string) => (
    <input
      type="range"
      min="0"
      max="1"
      step="0.1"
      value={isMuted ? 0 : volume}
      onChange={(e) => {
        const next = parseFloat(e.target.value);
        setIsMuted(next === 0);
        onVolumeChange(next);
      }}
      className={widthClass}
      title="Volume"
      aria-label="Volume"
    />
  );

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      // The browser answered (entered, or exited e.g. with Esc), so the
      // "did it really go fullscreen?" check is no longer needed.
      window.clearTimeout(fullscreenCheckRef.current);
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // CSS fallback: Escape leaves it like it does real fullscreen, and the page
  // behind is locked so iOS doesn't scroll or bounce it under the lesson.
  useEffect(() => {
    if (!isPseudoFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsPseudoFullscreen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPseudoFullscreen]);

  // Audio event handlers
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const audio = audioRef.current;
      const time = audio.currentTime;
      const dur = audio.duration;
      setCurrentTime(time);
      setAudioDuration(dur);
      if (dur > 0 && isFinite(dur)) {
        setIsAudioReady(true);
      }

      // Resume after a language switch. Needs the length, which only exists
      // once metadata has loaded — this handler is also wired to
      // onLoadedMetadata/onCanPlay, so it is the first place that knows it.
      const resume_to = initial_ratio_ref.current;
      if (!resume_applied_ref.current && resume_to !== undefined && resume_to > 0 && dur > 0 && isFinite(dur)) {
        resume_applied_ref.current = true;
        try {
          // Kept short of the end so a rounded-up ratio cannot fire 'ended'.
          audio.currentTime = Math.min(resume_to * dur, Math.max(dur - 0.5, 0));
          if (auto_play_ref.current) audio.play().catch(() => {});
        } catch { /* a refused seek just leaves it at the start */ }
        return; // the seek raises another timeupdate; report from that one
      }

      // Reported as a RATIO — the same lesson is a different length per language.
      if (onProgressRatio && dur > 0 && isFinite(dur)) {
        onProgressRatio(Math.min(Math.max(time / dur, 0), 1), !audio.paused);
      }
      const progressPercentage = dur > 0 ? (time / dur) * 100 : 0;
      setProgress(progressPercentage);
      setAudioProgress(progressPercentage);

      // Report progress based on BEST of: scroll progress, audio progress, or time progress
      // This ensures progress reaches 100% when user scrolls to bottom OR completes audio
      if (onProgressChange) {
        let reportedProgress: number;

        if (audioUrl) {
          // With audio: Use the MAXIMUM of scroll, audio, or time progress
          // This way scrolling to bottom OR listening to audio will show 100%
          reportedProgress = Math.floor(Math.max(scrollProgress, progressPercentage, viewTimeProgress));
        } else {
          // No audio: Use the MAXIMUM of scroll or time progress
          reportedProgress = Math.floor(Math.max(scrollProgress, viewTimeProgress));
        }

        // Cap at 100%
        reportedProgress = Math.min(100, reportedProgress);

        onProgressChange(reportedProgress);
      }
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * audioDuration;

    audioRef.current.currentTime = newTime;
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = async () => {
    if (!containerRef.current) {
      return;
    }

    // A check left over from an earlier toggle must not fire after this one.
    window.clearTimeout(fullscreenCheckRef.current);

    if (isPseudoFullscreen) {
      setIsPseudoFullscreen(false);
      return;
    }

    const getFullscreenElement = () =>
      document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement;

    try {
      if (!getFullscreenElement()) {
        const el = containerRef.current as any;
        const request = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
        const enabled = document.fullscreenEnabled || (document as any).webkitFullscreenEnabled;

        // iPhone (Safari and every other iOS browser — they are all WebKit)
        // only lets <video> go fullscreen: the API is missing or disabled.
        if (!request || !enabled) {
          setIsPseudoFullscreen(true);
          return;
        }
        await request.call(el);
        // Some mobile WebKit builds accept the call and then do nothing.
        fullscreenCheckRef.current = window.setTimeout(() => {
          if (!getFullscreenElement()) setIsPseudoFullscreen(true);
        }, 400);
      } else {
        // Exit fullscreen with browser compatibility
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error);
      // Entering was refused (e.g. inside a restricted frame): cover the page instead.
      if (!getFullscreenElement()) {
        setIsPseudoFullscreen(true);
      }
    }
  };

  // Keyboard scrolling (arrows / PgUp / PgDn / Space) only reaches the document
  // that has focus. The lesson lives in an iframe, so until the learner clicked
  // into it the keys went to the player around it — which has nothing to
  // scroll — and looked dead. Hand focus to the lesson once it loads, unless
  // the learner is already typing somewhere (e.g. the AI chat input).
  const focusLessonFrame = () => {
    const active = document.activeElement as HTMLElement | null;
    const isTyping = !!active && (
      active.tagName === 'INPUT' ||
      active.tagName === 'TEXTAREA' ||
      active.tagName === 'SELECT' ||
      active.isContentEditable
    );
    if (isTyping) return;
    try {
      iframeRef.current?.contentWindow?.focus();
    } catch {
      // Focus is best-effort; ignore browsers that refuse it.
    }
  };

  // Single container approach - keep the same DOM element for fullscreen
  return (
    <div
      ref={containerRef}
      className={isExpanded ? "fixed inset-0 z-[70] bg-black flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]" : "flex flex-col h-full"}
    >
      {/* Audio element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleTimeUpdate}
          onDurationChange={handleTimeUpdate}
          onCanPlay={handleTimeUpdate}
          preload="metadata"
        />
      )}

      {/* Audio Controls at Top - Compact */}
      {audioUrl && (
        <div className={isExpanded ? "bg-black/80 text-white flex-shrink-0 px-4 py-2" : "bg-gradient-to-r from-brand-purple-600 to-brand-indigo-600 text-white shadow-sm flex-shrink-0 px-3 py-2"}>
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPauseClick}
              className="p-1.5 bg-white/20 hover:bg-white/30 rounded text-white flex-shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            </button>

            {/* Progress Bar with Time — show a loader until the audio metadata
                is ready instead of a misleading 0:00. */}
            {isAudioReady ? (
              <div className="flex-1 min-w-0 flex items-center gap-1.5 sm:gap-2">
                <span className="text-xs text-white/80 sm:min-w-[40px]">{formatTime(currentTime)}</span>
                <div
                  className="flex-1 min-w-0 h-1.5 rounded-full cursor-pointer bg-white/20"
                  onClick={handleProgressClick}
                >
                  <div
                    className="h-full bg-white rounded-full shadow-sm"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-xs text-white/80 sm:min-w-[40px]">{formatTime(audioDuration)}</span>
              </div>
            ) : (
              <div className="flex-1 min-w-0 flex items-center gap-2 text-white/80">
                {/* Not ready and paused: iPhone loads nothing until play is pressed, so a spinner would spin forever — invite the tap instead. */}
                {isPlaying ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="text-xs truncate">Loading audio…</span>
                  </>
                ) : (
                  <span className="text-xs truncate">Press play to listen</span>
                )}
              </div>
            )}

            {/* Compact Controls */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
              <button
                onClick={handleRestart}
                className="p-1 rounded text-white bg-white/10 hover:bg-white/20 transition-colors"
                title="Restart"
              >
                <RotateCcw className="w-3 h-3" />
              </button>

              <button
                onClick={onPlaybackRateChange}
                className="px-2 py-0.5 text-xs font-medium rounded text-white bg-white/10 hover:bg-white/20 transition-colors"
                title="Playback Speed"
              >
                {playbackRate}x
              </button>

              {/* Mute works on every device — iOS ignores audio.volume (there the
                  hardware buttons set the level) but honours audio.muted.
                  sm and up: mute button + slider inline. Phones: one row has no
                  room for the slider, so the speaker opens it in a pop-up. */}
              <div ref={volumeRef} className="relative flex items-center gap-1">
                <button
                  onClick={() => setShowVolumePopup(v => !v)}
                  className="sm:hidden p-1 rounded text-white bg-white/10 hover:bg-white/20 transition-colors"
                  title="Volume"
                  aria-label="Volume"
                  aria-expanded={showVolumePopup}
                >
                  {isMuted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={toggleMute}
                  className="hidden sm:inline-flex p-1 rounded text-white bg-white/10 hover:bg-white/20 transition-colors"
                  title={isMuted ? 'Unmute' : 'Mute'}
                  aria-label={isMuted ? 'Unmute' : 'Mute'}
                >
                  {isMuted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                </button>
                <span className="hidden sm:inline-flex">{renderVolumeSlider('w-16')}</span>

                {showVolumePopup && (
                  <div className="sm:hidden absolute top-full right-0 mt-2 z-20 flex items-center gap-2 px-3 py-2 bg-gray-900/95 rounded-lg shadow-lg">
                    <button
                      onClick={toggleMute}
                      className="p-1.5 rounded text-white bg-white/10 hover:bg-white/20 transition-colors"
                      title={isMuted ? 'Unmute' : 'Mute'}
                      aria-label={isMuted ? 'Unmute' : 'Mute'}
                    >
                      {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                    {renderVolumeSlider('w-28')}
                  </div>
                )}
              </div>

              {!isExpanded && (
                <button
                  onClick={toggleFullscreen}
                  className="p-1 bg-white/10 hover:bg-white/20 rounded transition-colors"
                  title="Fullscreen"
                >
                  <Maximize2 className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* HTML Content Display */}
      <div className={isExpanded ? "relative flex-1 overflow-hidden bg-white" : "relative flex-1 overflow-hidden bg-gradient-to-r from-purple-50 to-indigo-50"}>
        {pdfUrl ? (
          // Uploaded PDF: NON-sandboxed iframe so Chrome's PDF viewer renders
          // (it is disabled inside sandboxed frames). Hide the viewer chrome and
          // fit to width.
          <iframe
            ref={iframeRef}
            src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
            className="w-full h-full border-none bg-white"
            title={title}
            allow="fullscreen"
            allowFullScreen
          />
        ) : (
          <iframe
            // A new URL (e.g. another language of the same lesson) gets a fresh
            // frame, so the old page can never linger inside a reused one.
            key={htmlUrl}
            ref={iframeRef}
            src={htmlUrl}
            className="w-full h-full border-none"
            title={title}
            onLoad={focusLessonFrame}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
            allow="fullscreen"
            />
        )}

        {/* Lessons without narration have no control bar, so they get their own
            fullscreen button. Top-right, where the exit button sits in
            fullscreen and where learners look for it; right-5 keeps it clear
            of the lesson's scrollbar. */}
        {!isExpanded && !audioUrl && (
          <button
            onClick={toggleFullscreen}
            className="absolute top-3 right-5 p-2.5 bg-black/50 hover:bg-black/70 text-white rounded-full shadow-lg transition-colors"
            title="Fullscreen"
            aria-label="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        )}

        {/* Fullscreen Exit Button - only show in fullscreen */}
        {isExpanded && (
          <div className="absolute top-2 right-5 sm:top-4 sm:right-6">
            <button
              onClick={toggleFullscreen}
              className="p-2 sm:p-3 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors"
              title="Exit Fullscreen"
              aria-label="Exit Fullscreen"
            >
              <Minimize2 className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}